import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../helpers/constants";
import { ParticipantData } from "../../../pages/createBulkActivity/modelsAndSchemas/Models";
import apiInstance from "../../instances/apiInstance";
import {
  ActivityTypePayload,
  ModalityPayload,
  ParticipantSearchParams,
  SelectedParticipantPayload,
  SelectedParticipantResponse,
  TopicListPayload,
} from "./models";

export const useBulkInsertActivityTypesMutation = () => {
  return useMutation({
    mutationFn: async (payload: ActivityTypePayload) => {
      const { participants } = payload;
      const res = await apiInstance.post(
        API_ENDPOINTS.getActivityTypeListForBulkInsertActivity,
        {
          participants,
        },
      );
      return res.data;
    },
    retry: false,
  });
};

export const useBulkInsertModalityMutation = () => {
  return useMutation({
    mutationFn: async (payload: ModalityPayload) => {
      const { programTemplateIds, activityTypeId, participants } = payload;
      const response = await apiInstance.post(
        API_ENDPOINTS.getModalityListForBulkInsertActivity(activityTypeId),
        {
          programTemplateIds,
          participants,
        },
      );
      return response.data;
    },
    retry: false,
  });
};

export const useBulkInsertTopicListMutation = () => {
  return useMutation({
    mutationFn: async (payload: TopicListPayload) => {
      const { programTemplateIds, activityTypeKey, modalityKey, participants } =
        payload;

      if (!activityTypeKey || !modalityKey) {
        return [];
      }
      const queryParams = new URLSearchParams();
      queryParams.append("activityType", activityTypeKey);
      queryParams.append("modalityKey", modalityKey);

      let url = `${API_ENDPOINTS.getTopicListForBulkInsertActivity}?${queryParams.toString()}`;

      const res = await apiInstance.post(url, {
        participants,
        programTemplateIds,
      });

      return res.data;
    },
    retry: false,
  });
};

export const useSearchParticipantListMutation = () => {
  return useMutation({
    mutationFn: (params: ParticipantSearchParams) => {
      return apiInstance.post(API_ENDPOINTS.GET_BULK_PARTICIPANT_LIST, params);
    },
  });
};

export const useSelectedParticipantMutation = () => {
  return useMutation({
    mutationFn: async (
      params: SelectedParticipantPayload,
    ): Promise<SelectedParticipantResponse> => {
      const res = await apiInstance.post(
        API_ENDPOINTS.GET_BULK_SUMMARY_PARTICIPANT_LIST,
        params,
      );
      return res.data;
    },
  });
};

export const useGetBulkActivitiesResult = () => {
  const [params, setParams] = useState<string>("");

  const query = useQuery({
    queryKey: ["useGetBulkActivitiesResult", params],
    queryFn: async (): Promise<ParticipantData> => {
      if (!params) return {} as ParticipantData;
      const res = await apiInstance.get(
        API_ENDPOINTS.getBulkActivitiesResult(params),
      );
      return res.data;
    },
    enabled: !!params,
    retry: false,
  });

  const refetchWithParams = (newParams: string) => {
    setParams(newParams);

    query.refetch();
  };

  return { ...query, refetchWithParams };
};
